import React from 'react';
import { connect } from 'react-redux';

import { FORMATTER_STYLE, getFormatter } from '../../utils/formatter';
import { Alert, Button, Col, Grid, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import SummaryCard from '../summarycard/SummaryCard';
import {
  FrontShadingDevice,
  SideShadingDevice,
  TopShadingDevice,
} from './ShadingDevices';
import EditorCanvas from './EditorCanvas';
import SCControlPanel from './SCControlPanel';
import SCControlPanelTable from './SCControlPanelTable';

import { computeSC } from '../../redux/section/section_component_actions';
import { stringifyErrorMessage } from '../../utils/utility';
import { defaultRejectHandler } from '../../utils/utility';

import './sc_editor.css';

const verticesBounds = verts => {
  const xs = verts.map(p => parseFloat(p.x || 0));
  const ys = verts.map(p => parseFloat(p.y || 0));
  return {
    minX: Math.min(...xs), maxX: Math.max(...xs),
    minY: Math.min(...ys), maxY: Math.max(...ys),
  };
};
// Max extent from origin — used for 3D group/axis sizing
const verticesMaxX = verts => { const { maxX } = verticesBounds(verts); return maxX || 1; };
const verticesMaxY = verts => { const { maxY } = verticesBounds(verts); return maxY || 1; };

export class SCEditor extends React.Component {
  constructor(props) {
    super(props);

    const initialVertices = (this.props.window && this.props.window.vertices) || [];
    const bboxW = initialVertices.length ? verticesMaxX(initialVertices) : this.props.window.width;
    const bboxH = initialVertices.length ? verticesMaxY(initialVertices) : this.props.window.height;

    this.state = {
      sc: props.SC,
      isShowErrorMessage: false,
      viewMode: 'card',
      viewModeBeforePolygon: 'card',
      leftDevices: this.props.shadingDevices.left,
      rightDevices: this.props.shadingDevices.right,
      topDevices: this.props.shadingDevices.top,
      frontDevices: this.props.shadingDevices.front,
      windowWidth: bboxW,
      windowHeight: bboxH,
      windowVertices: (this.props.window && this.props.window.vertices) || [],
      windowMode: (this.props.window && this.props.window.vertices) ? 'polygon' : 'rectangle',
      focusedWindowVertexIndex: null,
      // { deviceType: 'left'|'right'|'top'|'front', devName, vertexIndex }
      focusedDeviceVertex: null,
    };

    this.controlPanelRef = null;
    this.controlPanelTableRef = null;
  }

  getData() {
    const computePolygonArea = vertices => {
      if (!vertices || vertices.length < 3) return 0;
      let sum = 0;
      for (let i = 0; i < vertices.length; i++) {
        const p1 = vertices[i];
        const p2 = vertices[(i + 1) % vertices.length];
        sum += (p1.x * p2.y) - (p2.x * p1.y);
      }
      return Math.abs(sum) / 2.0;
    };
    const isPolygon = this.state.windowVertices && this.state.windowVertices.length >= 3;
    const area = isPolygon
      ? computePolygonArea(this.state.windowVertices)
      : this.state.windowWidth * this.state.windowHeight;
    return {
      area,
      SC: this.state.sc.total,
      rawSC: this.state.sc,
      structure: {
        window: isPolygon
          ? { vertices: this.state.windowVertices }
          : { width: this.state.windowWidth, height: this.state.windowHeight },
        shadingDevices: {
          left: this.state.leftDevices,
          right: this.state.rightDevices,
          top: this.state.topDevices,
          front: this.state.frontDevices,
        },
      },
    };
  }

  onComponentChanged(data) {
    this.setState(data);
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.isCalculatingSC && nextProps.isCalculatingSC !== this.props.isCalculatingSC && !nextProps.errorMessage) {
      this.setState({ sc: nextProps.newSC });
    }

    if (nextProps.errorMessage && this.props.errorMessage !== nextProps.errorMessage) {
      this.setState({ isShowErrorMessage: true });
    }
  }

  renderSCInfo() {
    const { t } = this.props;
    const {
      sc: { total, diffuse, beam },
    } = this.state;

    const formatValue = val => val ? getFormatter(FORMATTER_STYLE.floatFractionDigit6)(val) : t('n/a');

    return (
      <div className="sc-info-summary">
        <div className="summary-card total">
          <div className="icon">
            <i className="fas fa-sun" />
          </div>
          <div className="info">
            <div className="label">{t('total')}</div>
            <div className="value">{formatValue(total)}</div>
          </div>
        </div>
        <div className="summary-card beam">
          <div className="icon">
            <i className="fas fa-long-arrow-alt-left" />
          </div>
          <div className="info">
            <div className="label">{t('beam')}</div>
            <div className="value">{formatValue(beam)}</div>
          </div>
        </div>
        <div className="summary-card diffuse">
          <div className="icon">
            <i className="fa fa-certificate" />
          </div>
          <div className="info">
            <div className="label">{t('diffuse')}</div>
            <div className="value">{formatValue(diffuse)}</div>
          </div>
        </div>
      </div>
    );
  }

  renderWindow() {
    const { windowWidth, windowHeight, windowVertices, windowMode, focusedWindowVertexIndex } = this.state;
    const isPolygon = (windowMode === 'polygon' || windowMode === 'circle') && windowVertices && windowVertices.length >= 3;
    if (!isPolygon) {
      return (
        <div
          className="window"
          style={{ width: `${windowWidth}em`, height: `${windowHeight}em` }}
        />
      );
    }
    // For polygon: x = Y axis (width), y = Z axis (height)
    // Use full bounds to support negative coordinates
    const { minX, maxX, minY, maxY } = verticesBounds(windowVertices);
    const trueW = (maxX - minX) || 1;
    const trueH = (maxY - minY) || 1;
    // SVG viewBox starts at (minX, minY) so negative coords render correctly
    const pointsAttr = windowVertices.map(p => `${parseFloat(p.x || 0)} ${parseFloat(p.y || 0)}`).join(' ');
    const markerPx = 8;
    return (
      <div
        className="window window-polygon"
        style={{
          width: `${trueW}em`,
          height: `${trueH}em`,
          // Offset so that coordinate (0,0) aligns with the scene origin (axis intersection)
          left: `${minX}em`,
          top: `${minY}em`,
        }}
      >
        {/* Polygon shape — viewBox uses actual coord space including negatives */}
        <svg
          style={{ position: 'absolute', left: 0, top: 0, width: '100%', height: '100%' }}
          viewBox={`${minX} ${minY} ${trueW} ${trueH}`}
          preserveAspectRatio="none"
        >
          <polygon points={pointsAttr} fill="#c9dbdc" stroke="gray" strokeWidth="0.03" />
        </svg>
        {/* Show marker only for the currently focused input row */}
        {focusedWindowVertexIndex !== null && windowVertices[focusedWindowVertexIndex] && (() => {
          const p = windowVertices[focusedWindowVertexIndex];
          const x = parseFloat(p.x || 0);
          const y = parseFloat(p.y || 0);
          return (
            <div
              key="focused-vertex"
              className="window-polygon-vertex"
              style={{
                position: 'absolute',
                left: `${((x - minX) / trueW) * 100}%`,
                top: `${((y - minY) / trueH) * 100}%`,
                width: markerPx * 2,
                height: markerPx * 2,
                borderRadius: '50%',
                background: '#2c5282',
                border: '2px solid white',
                transform: 'translate(-50%, -50%) rotateX(180deg)',
                pointerEvents: 'none',
                zIndex: 1,
                boxShadow: '0 2px 8px rgba(44,82,130,0.6)',
              }}
            />
          );
        })()}
      </div>
    );
  }

  renderShadingDevices() {
    const { leftDevices, rightDevices, frontDevices, topDevices, focusedDeviceVertex: fv } = this.state;
    const getFocus = (deviceType, name) =>
      fv && fv.deviceType === deviceType && fv.devName === name ? fv.vertexIndex : null;
    const windowAzimuth = this.props.azimuth || 0;

    return (
      <React.Fragment>
        {leftDevices && leftDevices.map(props => (
          <SideShadingDevice side="left" {...props} key={props.name} focusedVertexIndex={getFocus('left', props.name)} windowAzimuth={windowAzimuth} />
        ))}
        {rightDevices && rightDevices.map(props => (
          <SideShadingDevice side="right" {...props} key={props.name} focusedVertexIndex={getFocus('right', props.name)} windowAzimuth={windowAzimuth} />
        ))}
        {frontDevices && frontDevices.map(props => (
          <FrontShadingDevice {...props} key={props.name} focusedVertexIndex={getFocus('front', props.name)} />
        ))}
        {topDevices && topDevices.map(props => (
          <TopShadingDevice {...props} key={props.name} focusedVertexIndex={getFocus('top', props.name)} />
        ))}
      </React.Fragment>
    );
  }

  renderErrorMessage(errorMessage) {
    if (!errorMessage) return null;
    return (
      <Alert
        bsStyle="danger"
        style={{ 'white-space': 'pre' }}
        onDismiss={() => this.setState({ isShowErrorMessage: false })}
      >
        {errorMessage}
      </Alert>
    );
  }

  render() {
    const { isCalculatingSC, errorMessage, t } = this.props;
    const { isShowErrorMessage, windowWidth, windowHeight, viewMode, viewModeBeforePolygon, windowVertices, windowMode } = this.state;
    const displayValue = (windowMode === 'polygon' || windowMode === 'circle') ? 'polygon' : viewMode;
    const window = (windowMode === 'polygon' || windowMode === 'circle')
      ? { vertices: windowVertices }
      : { width: this.state.windowWidth, height: this.state.windowHeight };
    const devices = {
      left: this.state.leftDevices,
      right: this.state.rightDevices,
      top: this.state.topDevices,
      front: this.state.frontDevices,
    };
    return (
      <div className="sc-visualized-display">
        {this.renderSCInfo()}
        {isShowErrorMessage && errorMessage && this.renderErrorMessage(stringifyErrorMessage(errorMessage))}
        <div className="sc-editor-main-layout">
          {/* Left side - 3D Canvas */}
          <div className="sc-editor-canvas-section">
            <EditorCanvas
              width={windowWidth}
              height={windowHeight}
            >
              {this.renderWindow()}
              {this.renderShadingDevices()}
            </EditorCanvas>
          </div>
          {/* Right side - Control Panel */}
          <div className="sc-editor-control-section">
            <div className="control-header">
              <ToggleButtonGroup
                name="viewMode"
                type="radio"
                value={viewMode}
                onChange={value => this.setState({ viewMode: value })}
              >
                <ToggleButton name="viewMode" value="card">
                  <i className="fa fa-grip-horizontal" />
                </ToggleButton>
                <ToggleButton name="viewMode" value="table">
                  <i className="fa fa-table" />
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                className="btn-calculate"
                disabled={isCalculatingSC}
                onClick={() => {
                  // Use latest data directly from the active control panel (card or table)
                  const ref =
                    this.controlPanelRef && this.controlPanelRef.getCurrentData
                      ? this.controlPanelRef
                      : this.controlPanelTableRef && this.controlPanelTableRef.getCurrentData
                        ? this.controlPanelTableRef
                        : null;
                  const latest = ref ? ref.getCurrentData() : null;
                  const payload = latest ? { ...this.state, ...latest } : this.state;
                  this.props.computeSC(payload);
                }}
              >
                {isCalculatingSC ? `${t('calculating')}...` : t('calculate')}
              </Button>
            </div>
            <div className="control-body">
              {viewMode === 'card' ? (
                <SCControlPanel
                  window={window}
                  windowMode={windowMode}
                  shadingDevices={devices}
                  onDataChanged={data => this.onComponentChanged(data)}
                  onFocusWindowVertex={index => this.setState({ focusedWindowVertexIndex: index })}
                  onFocusDeviceVertex={(deviceType, devName, index) =>
                    this.setState({ focusedDeviceVertex: index !== null ? { deviceType, devName, vertexIndex: index } : null })
                  }
                  onReady={ref => { this.controlPanelRef = ref; }}
                />
              ) : (
                <SCControlPanelTable
                  window={window}
                  windowMode={windowMode}
                  shadingDevices={devices}
                  onDataChanged={data => this.onComponentChanged(data)}
                  onReady={ref => { this.controlPanelTableRef = ref; }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

SCEditor.defaultProps = {
  azimuth: 0,
  window: null,
  shadingDevices: null,
  SC: null,
};

const mapStateToProps = state => ({
  isCalculatingSC: state.section_component.isCalculatingSC,
  newSC: state.section_component.SC,
  errorMessage: state.section_component.SCErrorMessage,
});

const mapDispatchToProps = (dispatch, { sectionComponentId, azimuth }) => ({
  computeSC: ({ windowWidth, windowHeight, windowVertices, windowMode, leftDevices, rightDevices, topDevices, frontDevices }) => {
    const windowParam = (windowMode === 'polygon' && windowVertices && windowVertices.length >= 3)
      ? { vertices: windowVertices }
      : { width: windowWidth, height: windowHeight };
    return defaultRejectHandler(
      dispatch(
        computeSC(sectionComponentId, azimuth, windowParam, leftDevices, rightDevices, topDevices, frontDevices)
      ),
    );
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { withRef: true },
)(SCEditor);



// WEBPACK FOOTER //
// ./src/components/sc_editor/SCEditor.js